import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  DocumentTextIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  Client,
  Company,
  CustomerPurchaseOrder,
  Deal,
  Product,
  catalogueAPI,
  clientsAPI,
  companiesAPI,
  customerPurchaseOrdersAPI,
  dealsAPI,
} from '../../services/api';
import { Badge, Button, Card, CardBody, EmptyState, Modal, PageHero, Spinner } from '../ui';
import InvoiceGeneratorModal, { type InvoiceGenerationResult } from '../invoices/InvoiceGeneratorModal';

const formatDate = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatFileSize = (bytes?: number) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatUploader = (po: CustomerPurchaseOrder) => {
  if (po.uploadedByUser) {
    const fullName = [po.uploadedByUser.firstName, po.uploadedByUser.lastName].filter(Boolean).join(' ');
    return fullName || po.uploadedByUser.email;
  }
  return po.uploadedBy ? `User #${po.uploadedBy}` : '-';
};

const CustomerPurchaseOrdersPage: React.FC = () => {
  const [purchaseOrders, setPurchaseOrders] = useState<CustomerPurchaseOrder[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceDealId, setInvoiceDealId] = useState<number | undefined>();
  const [filters, setFilters] = useState({
    search: '',
    clientId: '',
    productId: '',
    uploadedByMe: false,
  });
  const [uploadForm, setUploadForm] = useState({
    dealId: '',
    name: '',
    description: '',
    file: null as File | null,
  });
  useEffect(() => {
    loadReferenceData();
  }, []);

  useEffect(() => {
    loadPurchaseOrders();
  }, [filters.clientId, filters.productId, filters.uploadedByMe]);

  useEffect(() => {
    const timer = setTimeout(loadPurchaseOrders, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const loadReferenceData = async () => {
    try {
      const [clientData, productData, dealData, companyData] = await Promise.all([
        clientsAPI.getAll(),
        catalogueAPI.getProducts({ isActive: true }),
        dealsAPI.getAll(),
        companiesAPI.getAll(),
      ]);
      const activeCompanies = companyData.filter((company) => company.isActive);
      setClients(Array.isArray(clientData) ? clientData : (clientData as any).data || []);
      setProducts(productData);
      setDeals(dealData);
      setCompanies(activeCompanies);
    } catch (loadError: any) {
      setError(loadError.response?.data?.message || 'Failed to load invoicing references');
    }
  };

  const loadPurchaseOrders = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await customerPurchaseOrdersAPI.getAll({
        search: filters.search.trim() || undefined,
        clientId: filters.clientId ? Number(filters.clientId) : undefined,
        productId: filters.productId ? Number(filters.productId) : undefined,
        uploadedByMe: filters.uploadedByMe || undefined,
      });
      setPurchaseOrders(data);
    } catch (loadError: any) {
      setError(loadError.response?.data?.message || 'Failed to load customer purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const accessibleDeals = useMemo(
    () => deals.filter((deal) => deal.client || deal.orderNumber || deal.description),
    [deals],
  );

  const openInvoiceModal = (dealId?: number) => {
    setError('');
    setInvoiceDealId(dealId);
    setShowInvoiceModal(true);
  };

  const closeInvoiceModal = () => {
    setShowInvoiceModal(false);
    setInvoiceDealId(undefined);
  };

  const handleInvoiceSuccess = async (result: InvoiceGenerationResult) => {
    setMessage(result.message);
    await loadPurchaseOrders();
    window.setTimeout(() => setMessage(''), result.action === 'email' ? 4000 : 3000);
  };

  const handleUpload = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!uploadForm.dealId || !uploadForm.file) {
      setError('Select a deal and choose a PO file');
      return;
    }

    try {
      setUploading(true);
      setError('');
      await dealsAPI.uploadDocument(Number(uploadForm.dealId), uploadForm.file, {
        documentType: 'PURCHASE_ORDER',
        name: uploadForm.name.trim() || uploadForm.file.name,
        description: uploadForm.description.trim() || undefined,
      });
      setShowUploadModal(false);
      setUploadForm({ dealId: '', name: '', description: '', file: null });
      setMessage('Customer PO uploaded and matched to the deal products');
      await loadPurchaseOrders();
      setTimeout(() => setMessage(''), 3000);
    } catch (uploadError: any) {
      setError(uploadError.response?.data?.message || 'Failed to upload customer PO');
    } finally {
      setUploading(false);
    }
  };

  const clearFilters = () => {
    setFilters({ search: '', clientId: '', productId: '', uploadedByMe: false });
  };

  return (
    <div className="px-4 py-8 sm:px-6 lg:px-8" data-tour="page-content">
      <PageHero
        title="Invoicing"
        subtitle="Generate customer invoices and match purchase orders to deals and products"
        icon={<DocumentTextIcon className="h-5 w-5 text-white drop-shadow-lg lg:h-6 lg:w-6" />}
        accentColor="blue"
      >
        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            variant="primary"
            onClick={() => openInvoiceModal()}
            leftIcon={<DocumentArrowDownIcon className="h-5 w-5" />}
          >
            Generate Invoice
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowUploadModal(true)}
            leftIcon={<PlusIcon className="h-5 w-5" />}
          >
            Upload PO
          </Button>
        </div>
      </PageHero>

      {error && (
        <div className="mt-4 rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm font-medium text-error-700">
          {error}
        </div>
      )}
      {message && (
        <div className="mt-4 rounded-lg border border-success-200 bg-success-50 px-4 py-3 text-sm font-medium text-success-700">
          {message}
        </div>
      )}

      <Card variant="elevated" className="mt-6">
        <CardBody className="p-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            <div className="relative lg:col-span-4">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon className="h-5 w-5 text-neutral-400" />
              </div>
              <input
                type="text"
                value={filters.search}
                onChange={(event) => setFilters({ ...filters, search: event.target.value })}
                placeholder="Search invoice PO, client, deal..."
                className="block w-full rounded-lg border border-neutral-300 py-2.5 pl-10 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="relative lg:col-span-3">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <FunnelIcon className="h-5 w-5 text-neutral-400" />
              </div>
              <select
                value={filters.clientId}
                onChange={(event) => setFilters({ ...filters, clientId: event.target.value })}
                className="block w-full rounded-lg border border-neutral-300 py-2.5 pl-10 pr-10 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Clients</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>
            <div className="lg:col-span-3">
              <select
                value={filters.productId}
                onChange={(event) => setFilters({ ...filters, productId: event.target.value })}
                className="block w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Products</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>{product.name} ({product.sku})</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 lg:col-span-1">
              <input
                type="checkbox"
                checked={filters.uploadedByMe}
                onChange={(event) => setFilters({ ...filters, uploadedByMe: event.target.checked })}
                className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              />
              Mine
            </label>
            <div className="flex justify-end lg:col-span-1">
              <button type="button" onClick={clearFilters} className="inline-flex rounded-md p-2 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900" title="Clear filters">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card variant="elevated" className="mt-6">
        <CardBody className="p-0">
          {loading ? (
            <div className="flex h-64 items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : purchaseOrders.length === 0 ? (
            <EmptyState
              icon={<DocumentTextIcon className="h-12 w-12" />}
              title="No customer purchase orders"
              description="Upload a customer PO from this page, a deal, or while generating an invoice."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase text-neutral-500">PO Name</th>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase text-neutral-500">Client</th>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase text-neutral-500">Deal</th>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase text-neutral-500">Products</th>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase text-neutral-500">Uploaded By</th>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase text-neutral-500">Uploaded</th>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase text-neutral-500">Size</th>
                    <th className="px-5 py-3 text-right text-xs font-medium uppercase text-neutral-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200 bg-white">
                  {purchaseOrders.map((po) => (
                    <tr key={po.id} className="hover:bg-neutral-50">
                      <td className="px-5 py-4 text-sm font-medium text-neutral-900">{po.name}</td>
                      <td className="px-5 py-4 text-sm text-neutral-700">{po.deal?.client?.name || '-'}</td>
                      <td className="px-5 py-4 text-sm text-neutral-700">{po.deal?.orderNumber || `Deal #${po.dealId}`}</td>
                      <td className="px-5 py-4 text-sm text-neutral-700">
                        <div className="flex max-w-sm flex-wrap gap-1">
                          {po.products.length === 0 ? '-' : po.products.map((product) => (
                            <Badge key={product.id} variant="outline" size="sm">{product.sku}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-neutral-700">{formatUploader(po)}</td>
                      <td className="px-5 py-4 text-sm text-neutral-700">{formatDate(po.uploadedAt)}</td>
                      <td className="px-5 py-4 text-sm text-neutral-700">{formatFileSize(po.fileSize)}</td>
                      <td className="px-5 py-4 text-right">
                        <div className="inline-flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openInvoiceModal(po.dealId)}
                            leftIcon={<DocumentArrowDownIcon className="h-4 w-4" />}
                          >
                            Invoice
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => customerPurchaseOrdersAPI.download(po)}
                            leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
                          >
                            Download
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        title="Upload Customer PO"
        size="lg"
      >
        <form onSubmit={handleUpload} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700">Deal *</label>
            <select
              value={uploadForm.dealId}
              onChange={(event) => setUploadForm({ ...uploadForm, dealId: event.target.value })}
              required
              className="mt-1 block w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select deal</option>
              {accessibleDeals.map((deal) => (
                <option key={deal.id} value={deal.id}>
                  {deal.orderNumber || `Deal #${deal.id}`} - {deal.client?.name || deal.description}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700">PO Name</label>
            <input
              value={uploadForm.name}
              onChange={(event) => setUploadForm({ ...uploadForm, name: event.target.value })}
              placeholder="Defaults to file name"
              className="mt-1 block w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700">Description</label>
            <textarea
              value={uploadForm.description}
              onChange={(event) => setUploadForm({ ...uploadForm, description: event.target.value })}
              rows={3}
              className="mt-1 block w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700">File *</label>
            <input
              type="file"
              required
              onChange={(event) => setUploadForm({ ...uploadForm, file: event.target.files?.[0] || null })}
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xlsx,.xls"
              className="mt-1 block w-full text-sm text-neutral-500 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100"
            />
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button type="button" variant="outline" onClick={() => setShowUploadModal(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={uploading}
              disabled={!uploadForm.dealId || !uploadForm.file}
              leftIcon={<DocumentArrowUpIcon className="h-4 w-4" />}
            >
              Upload PO
            </Button>
          </div>
        </form>
      </Modal>

      <InvoiceGeneratorModal
        isOpen={showInvoiceModal}
        onClose={closeInvoiceModal}
        deals={accessibleDeals}
        companies={companies}
        initialDealId={invoiceDealId}
        onSuccess={handleInvoiceSuccess}
      />
    </div>
  );
};

export default CustomerPurchaseOrdersPage;
